import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ArrowDownToLine, WifiOff, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, LoadingBlock, EmptyState, formatDateTime } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const METHOD_LABEL = { bank_transfer: { it: "Bonifico bancario", en: "Bank transfer", de: "Banküberweisung" }, crypto: { it: "Crypto", en: "Crypto", de: "Krypto" }, bitcoin: { it: "Deposito Bitcoin", en: "Bitcoin deposit", de: "Bitcoin-Einzahlung" }, ethereum: { it: "Ethereum / stablecoin", en: "Ethereum / stablecoin", de: "Ethereum / Stablecoin" } };
const DEP_STATUS = {
  PENDING_REVIEW: { it: "In verifica", en: "Under review", de: "In Prüfung" },
  CONFIRMING: { it: "In conferma", en: "Confirming", de: "Wird bestätigt" },
  CONFIRMED: { it: "Confermato", en: "Confirmed", de: "Bestätigt" },
  REJECTED: { it: "Rifiutato", en: "Rejected", de: "Abgelehnt" },
  CANCELLED: { it: "Annullato", en: "Cancelled", de: "Storniert" },
};

export default function Deposit() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de || en) : it);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [method, setMethod] = useState("");
  const [form, setForm] = useState({ amount: "", asset: "", network: "", tx_hash: "" });

  const load = () => {
    api.get("/financial/settings").then(({ data }) => {
      setSettings(data);
      if (data.deposit_methods?.[0]) setMethod(data.deposit_methods[0]);
      setForm((f) => ({ ...f, asset: data.supported_assets?.[0] || "", network: data.supported_networks?.[0] || "" }));
    });
    api.get("/deposits").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/portal/wallet").then(({ data }) => setWallet(data && data.address ? data : null)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (settings === null) return <LoadingBlock />;

  const enabled = settings.deposits_enabled && (settings.deposit_methods || []).length > 0;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success(L("Indirizzo copiato", "Address copied", "Adresse kopiert")); };
  const addr = wallet || (settings.deposit_addresses || []).find((a) => (method === "bitcoin" && a.asset === "BTC") || (method === "ethereum"));
  const isBank = method === "bank_transfer";

  const submit = async () => {
    try {
      await api.post("/deposits", { ...form, method, amount: parseFloat(form.amount) || 0, destination_address: addr?.address });
      toast.success(L("Deposito registrato (in verifica)", "Deposit submitted (under review)", "Einzahlung erfasst (in Prüfung)"));
      setForm({ amount: "", asset: settings.supported_assets?.[0] || "", network: settings.supported_networks?.[0] || "", tx_hash: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Deposito", "Deposit", "Einzahlung")}</h2>

      {!enabled ? (
        <Card className="p-8 text-center" data-testid="deposit-disabled">
          <WifiOff className="mx-auto h-8 w-8 text-slate-500" />
          <p className="mt-3 text-slate-300">{L("Depositi non ancora abilitati", "Deposits not yet enabled", "Einzahlungen noch nicht aktiviert")}</p>
          <p className="mt-1 text-sm text-slate-500">{L("Contatta l'assistenza per abilitare i depositi sul tuo account.", "Contact support to enable deposits on your account.", "Kontaktiere den Support, um Einzahlungen zu aktivieren.")}</p>
        </Card>
      ) : (
        <Card className="p-6 space-y-4">
          <div>
            <Label className="text-slate-300">{L("Metodo", "Method", "Methode")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {settings.deposit_methods.map((m) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`deposit-method-${m}`} className={`rounded-lg border px-3 py-2 text-sm ${method === m ? "border-cyanx text-cyanx bg-cyanx/10" : "border-white/10 text-slate-300"}`}>{METHOD_LABEL[m]?.[lang] || METHOD_LABEL[m]?.it || m}</button>
              ))}
            </div>
          </div>

          {!isBank && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">{L("Asset", "Asset", "Asset")}</Label>
                <select value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="deposit-asset">
                  {(settings.supported_assets || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div><Label className="text-slate-300">{L("Rete", "Network", "Netzwerk")}</Label>
                <select value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="deposit-network">
                  {(settings.supported_networks || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          )}

          {isBank ? (
            <div className="rounded-lg border border-white/10 bg-navy p-4 text-sm text-slate-300">
              {L("Riceverai le coordinate bancarie dopo l'invio della richiesta di deposito.", "You will receive bank details after submitting the deposit request.", "Du erhältst die Bankdaten nach dem Absenden der Einzahlungsanfrage.")}
            </div>
          ) : addr ? (
            <div className="rounded-lg border border-white/10 bg-navy p-4">
              <div className="flex items-center gap-4">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(addr.address)}`} alt="QR" className="rounded bg-white p-1" data-testid="deposit-qr" />
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">{L("Asset", "Asset", "Asset")}: {addr.asset} · {L("Rete", "Network", "Netzwerk")}: {addr.network}</p>
                  <p className="mt-1 font-mono text-xs text-white break-all" data-testid="deposit-address">{addr.address}</p>
                  <Button size="sm" variant="outline" className="mt-2 border-white/20" onClick={() => copy(addr.address)} data-testid="deposit-copy"><Copy className="h-3.5 w-3.5 mr-1.5" />{L("Copia indirizzo", "Copy address", "Adresse kopieren")}</Button>
                  {addr.min ? <p className="mt-2 text-xs text-slate-500">{L("Minimo", "Minimum", "Minimum")}: {addr.min}</p> : null}
                  <p className="text-xs text-slate-500">{L("Conferme richieste", "Confirmations", "Bestätigungen")}: {addr.confirmations || settings.required_confirmations}</p>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded border border-gold/30 bg-gold/10 p-2 text-xs text-gold">
                <ShieldAlert className="h-4 w-4 shrink-0" />{L("Invia solo l'asset sulla rete corretta. Invii sulla rete sbagliata possono comportare la perdita dei fondi.", "Send only the asset on the correct network. Wrong-network transfers may result in loss of funds.", "Sende nur den Asset im richtigen Netzwerk. Falsche Netzwerke können zum Verlust der Mittel führen.")}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="deposit-no-wallet">{L("Wallet di deposito non ancora configurato. Contatta l'assistenza.", "Deposit wallet not yet configured. Contact support.", "Einzahlungs-Wallet noch nicht konfiguriert. Kontaktiere den Support.")}</p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">{L("Importo", "Amount", "Betrag")}</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="deposit-amount" /></div>
            {!isBank && <div><Label className="text-slate-300">{L("TXID / hash transazione", "TXID / transaction hash", "TXID / Transaktions-Hash")}</Label><Input value={form.tx_hash} onChange={(e) => setForm({ ...form, tx_hash: e.target.value })} className="mt-1.5 bg-navy border-white/10 font-mono text-xs" data-testid="deposit-txhash" /></div>}
          </div>
          <p className="text-xs text-slate-500">{L("L'invio del TXID non accredita automaticamente il saldo: il deposito resta in verifica fino alla conferma dell'amministratore.", "Submitting a TXID does not auto-credit your balance: the deposit stays under review until an admin confirms it.", "Das Absenden einer TXID schreibt dein Guthaben nicht automatisch gut: Die Einzahlung bleibt bis zur Bestätigung in Prüfung.")}</p>
          <Button onClick={submit} disabled={!form.amount || (!isBank && !form.tx_hash)} className="bg-gold text-navy hover:bg-gold-hover" data-testid="deposit-submit"><ArrowDownToLine className="h-4 w-4 mr-1.5" />{L("Ho effettuato il deposito", "I have made the deposit", "Ich habe die Einzahlung getätigt")}</Button>
        </Card>
      )}

      <Card className="p-6">
        <h3 className="font-heading text-lg text-white mb-4">{L("Storico depositi", "Deposit history", "Einzahlungsverlauf")}</h3>
        {history.length === 0 ? <EmptyState title={L("Nessun deposito", "No deposits", "Keine Einzahlungen")} /> : (
          <div className="space-y-2">
            {history.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3 text-sm" data-testid={`deposit-${d.id}`}>
                <div><p className="text-white">{d.amount} {d.asset || ""} · <span className="text-slate-500">{d.network || d.method}</span></p><p className="text-xs text-slate-500 font-mono break-all">{d.tx_hash || "—"} · {formatDateTime(d.created_at)}</p></div>
                <span className="text-xs text-gold shrink-0 ml-2">{DEP_STATUS[d.status]?.[lang] || DEP_STATUS[d.status]?.it || d.status}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
